/**
 * Indicatori real time del server nell'header: disco, CPU, RAM, processi.
 * Polling ogni 3s tramite GET /sistema/stats (sospeso a tab nascosta).
 * Il credito residuo OpenRouter (GET /sistema/credito-ai, cache backend 5 min)
 * è verde da 10 in su, arancione tra 5 e 10, rosso a 5 o meno.
 */
import { Tag, Tooltip } from 'antd'
import { useQuery } from '@tanstack/react-query'
import client from '@/api/client'

interface SistemaStats {
  disco_totale_gb: number
  disco_usato_gb: number
  disco_libero_gb: number
  disco_percentuale: number
  cpu_percentuale: number
  ram_totale_gb: number
  ram_usata_gb: number
  ram_percentuale: number
  processi: number
}

const SOGLIA_CREDITO_VERDE = 10
const SOGLIA_CREDITO_ROSSO = 5

const coloreCredito = (residuo: number) =>
  residuo <= SOGLIA_CREDITO_ROSSO ? '#cf1322' : residuo < SOGLIA_CREDITO_VERDE ? 'orange' : 'green'

const colore = (pct: number) => (pct >= 90 ? 'red' : pct >= 70 ? 'orange' : 'green')

// Oltre questa soglia il tag del disco diventa a sfondo rosso pieno
const SOGLIA_DISCO_CRITICO = 90

export default function SistemaStats() {
  const { data } = useQuery({
    queryKey: ['sistema-stats'],
    queryFn: () => client.get<SistemaStats>('/sistema/stats').then(r => r.data),
    refetchInterval: 3_000,
    refetchIntervalInBackground: false,
  })
  const { data: credito } = useQuery({
    queryKey: ['sistema-credito-ai'],
    queryFn: () => client.get<{ residuo: number | null }>('/sistema/credito-ai').then(r => r.data),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  })
  if (!data) return null
  const residuo = credito?.residuo ?? null

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <Tooltip title={`Disco: ${data.disco_usato_gb} GB usati su ${data.disco_totale_gb} GB (${data.disco_libero_gb} GB liberi)`}>
        <Tag
          color={data.disco_percentuale > SOGLIA_DISCO_CRITICO ? '#cf1322' : colore(data.disco_percentuale)}
          style={{ margin: 0, fontWeight: data.disco_percentuale > SOGLIA_DISCO_CRITICO ? 600 : undefined }}
        >Disco {data.disco_percentuale}%</Tag>
      </Tooltip>
      <Tooltip title="Utilizzo CPU del server">
        <Tag color={colore(data.cpu_percentuale)} style={{ margin: 0 }}>CPU {data.cpu_percentuale}%</Tag>
      </Tooltip>
      <Tooltip title={`RAM: ${data.ram_usata_gb} GB usati su ${data.ram_totale_gb} GB (esclusa la cache, recuperabile)`}>
        <Tag color={colore(data.ram_percentuale)} style={{ margin: 0 }}>RAM {data.ram_percentuale}%</Tag>
      </Tooltip>
      <Tooltip title="Processi attivi nei container del gestionale (database, backend, worker, frontend, centralino…)">
        <Tag color="green" style={{ margin: 0 }}>Processi {data.processi}</Tag>
      </Tooltip>
      {residuo !== null && (
        <Tooltip title="Credito residuo OpenRouter (AI), aggiornato ogni 5 minuti. Verde da 10 in su, arancione tra 5 e 10, rosso a 5 o meno">
          <Tag
            color={coloreCredito(residuo)}
            style={{ margin: 0, fontWeight: residuo <= SOGLIA_CREDITO_ROSSO ? 600 : undefined }}
          >AI ${residuo.toFixed(2)}</Tag>
        </Tooltip>
      )}
    </div>
  )
}
